import { createContext, useContext, useState, useCallback, useEffect, ReactNode, useMemo } from "react";
import { startOfMonth, endOfDay } from "date-fns";
import { DateRange, Filters, FilterOptions, FilterOptionsResponse, ErrorLogsFilters } from "utils/analytics/types";
import { mapFilterOptions } from "utils/analytics/mapping";
import { DEFAULT_SELECTED_COLUMNS } from "utils/analytics/constants";

const COLUMN_STORAGE_KEY = "analytics_selected_columns";

type StoredColumns = { domain?: string[]; error?: string[]; version?: number };

// Bumped with each new default column; lists saved before it may still be the old default.
const COLUMNS_VERSION = 2;

const readStoredColumns = (): StoredColumns => {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(COLUMN_STORAGE_KEY) : null;
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

// The domain default before Cancelled Requests; a saved list matching it was never customised.
const PREVIOUS_DOMAIN_DEFAULT = ["domain", "requests", "successRate", "credits", "ultraPremium", "premium", "render", "aiParser"];

const isSameColumns = (a: string[], b: string[]) =>
  a.length === b.length && b.every((key) => a.includes(key));

const initialColumns = (tableType: "domain" | "error"): string[] => {
  const stored = readStoredColumns();
  const columns = stored[tableType];
  if (!Array.isArray(columns) || columns.length === 0) return DEFAULT_SELECTED_COLUMNS[tableType];
  const savedBeforeThisVersion = stored.version !== COLUMNS_VERSION;
  if (tableType === "domain" && savedBeforeThisVersion && isSameColumns(columns, PREVIOUS_DOMAIN_DEFAULT)) {
    return DEFAULT_SELECTED_COLUMNS.domain;
  }
  return columns;
};

const writeStoredColumns = (tableType: "domain" | "error", columns: string[]) => {
  try {
    if (typeof localStorage === "undefined") return;
    const next: StoredColumns = { ...readStoredColumns(), [tableType]: columns, version: COLUMNS_VERSION };
    localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / privacy-mode failures
  }
};

interface AnalyticsFilterContextType {
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  filters: Filters;
  setFilters: (filters: Filters) => void;
  filterOptions: FilterOptions;
  setFilterOptions: (filterOptions: FilterOptionsResponse | null | undefined) => void;

  // Error logs filters (persisted across navigation)
  errorLogsFilters: ErrorLogsFilters;
  setErrorLogsFilters: (filters: Partial<ErrorLogsFilters>) => void;

  // Column preferences (shared across overview + full pages)
  selectedDomainColumns: string[];
  setSelectedDomainColumns: React.Dispatch<React.SetStateAction<string[]>>;
  selectedErrorColumns: string[];
  setSelectedErrorColumns: React.Dispatch<React.SetStateAction<string[]>>;
}

interface AnalyticsFilterProviderProps {
  children: ReactNode;
}

const AnalyticsFilterContext = createContext<AnalyticsFilterContextType | undefined>(undefined);

export const AnalyticsFiltersProvider = ({ children }: AnalyticsFilterProviderProps) => {
  // Default to start of current month until end of today
  const defaultDateRange = useMemo<DateRange>(
    () => [startOfMonth(new Date()), endOfDay(new Date())],
    []
  );

  const [dateRange, setDateRange] = useState<DateRange>(defaultDateRange);
  const [filters, setFilters] = useState<Filters>({
    productType: null,
    parameters: [],
    domains: [],
    countries: [],
  });
  const [filterOptionsState, setFilterOptionsState] = useState<FilterOptions>({
    domainCount: 0,
    domains: [],
    countries: [],
    productTypes: [],
    parameters: [],
  });

  // Error logs filters — persisted here so they survive ErrorLogsProvider unmount
  const [errorLogsFiltersState, setErrorLogsFiltersState] = useState<ErrorLogsFilters>({
    searchTerm: "",
    severity: null,
    statusCodes: [],
    domains: [],
  });

  const setErrorLogsFilters = useCallback((newFilters: Partial<ErrorLogsFilters>) => {
    setErrorLogsFiltersState((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // Column preferences — hydrated from localStorage so tables show user prefs
  // on first paint without waiting for the customize-columns modal to open.
  // A write-through effect keeps localStorage in sync with state changes.
  const [selectedDomainColumns, setSelectedDomainColumns] = useState<string[]>(
    () => initialColumns("domain")
  );
  const [selectedErrorColumns, setSelectedErrorColumns] = useState<string[]>(
    () => initialColumns("error")
  );

  useEffect(() => {
    writeStoredColumns("domain", selectedDomainColumns);
  }, [selectedDomainColumns]);

  useEffect(() => {
    writeStoredColumns("error", selectedErrorColumns);
  }, [selectedErrorColumns]);

  const handleFilterOptions = useCallback(
    (filterOptions: FilterOptionsResponse | null | undefined) => {
      setFilterOptionsState(mapFilterOptions(filterOptions));
    },
    []
  );

  const value = useMemo<AnalyticsFilterContextType>(
    () => ({
      dateRange,
      setDateRange,
      filters,
      setFilters,
      filterOptions: filterOptionsState,
      setFilterOptions: handleFilterOptions,
      errorLogsFilters: errorLogsFiltersState,
      setErrorLogsFilters,
      selectedDomainColumns,
      setSelectedDomainColumns,
      selectedErrorColumns,
      setSelectedErrorColumns,
    }),
    [
      dateRange,
      filters,
      filterOptionsState,
      handleFilterOptions,
      errorLogsFiltersState,
      setErrorLogsFilters,
      selectedDomainColumns,
      selectedErrorColumns,
    ]
  );

  return (
    <AnalyticsFilterContext.Provider value={value}>
      {children}
    </AnalyticsFilterContext.Provider>
  );
};

export const useAnalyticsFilters = () => {
  const context = useContext(AnalyticsFilterContext);
  if (context === undefined) {
    throw new Error("useAnalyticsFilters must be used within an AnalyticsFiltersProvider");
  }
  return context;
};
